import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const DAYS = [
  { day: 'Mon', value: 1 },
  { day: 'Tue', value: 2 },
  { day: 'Wed', value: 3 },
  { day: 'Thu', value: 4 },
  { day: 'Fri', value: 5 },
  { day: 'Sat', value: 6 },
  { day: 'Sun', value: 7 }
];

/**
 * Format working days array for human-readable display
 * @param {number[]} days - Array of ISO day numbers (1=Mon, 7=Sun)
 * @returns {string} - Formatted string like "Mon-Fri" or "Mon, Wed, Fri"
 */
export function formatWorkingDays(days = []) {
  const dayNames = { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun' };

  if (!days || days.length === 0) return 'No working days';

  // Sort days to ensure consistent comparison
  const sortedDays = [...days].sort((a, b) => a - b);

  // Check for common patterns
  if (JSON.stringify(sortedDays) === JSON.stringify([1, 2, 3, 4, 5])) return 'Mon-Fri';
  if (JSON.stringify(sortedDays) === JSON.stringify([1, 2, 3, 4])) return 'Mon-Thu';
  if (JSON.stringify(sortedDays) === JSON.stringify([2, 3, 4, 5, 6])) return 'Tue-Sat';
  if (JSON.stringify(sortedDays) === JSON.stringify([1, 2, 3, 4, 5, 6])) return 'Mon-Sat';
  if (JSON.stringify(sortedDays) === JSON.stringify([1, 2, 3, 4, 5, 6, 7])) return 'Every day';

  // Check if days are consecutive
  const isConsecutive = sortedDays.every((day, i) => i === 0 || day === sortedDays[i - 1] + 1);
  if (isConsecutive && sortedDays.length >= 3) {
    return `${dayNames[sortedDays[0]]}-${dayNames[sortedDays[sortedDays.length - 1]]}`;
  }

  // Otherwise list individual days
  return sortedDays.map(d => dayNames[d]).join(', ');
}

/**
 * Reusable component for editing working days with visual toggles
 *
 * @param {Object} props
 * @param {number[]} props.value - Current working days array (e.g., [1,2,3,4,5])
 * @param {Function} props.onChange - Callback when days change: (newDays) => void
 * @param {number[]} props.companyDefault - Company's default working days
 * @param {boolean} props.useCompanyDefault - Whether currently using company default
 * @param {Function} props.onToggleDefault - Callback when toggle changes: (useDefault) => void
 * @param {boolean} props.disabled - Whether the editor is disabled
 * @param {boolean} props.showDefaultToggle - Whether to show the "use company default" toggle
 * @param {string} props.size - Button size: 'sm' | 'md' (default 'md')
 */
export function WorkingDaysEditor({
  value = [1, 2, 3, 4, 5],
  onChange,
  companyDefault = [1, 2, 3, 4, 5],
  useCompanyDefault = true,
  onToggleDefault,
  disabled = false,
  showDefaultToggle = true,
  size = 'md'
}) {
  const effectiveDays = useCompanyDefault ? companyDefault : value;

  const toggleDay = (dayValue) => {
    if (disabled || useCompanyDefault) return;

    let newDays;
    if (effectiveDays.includes(dayValue)) {
      newDays = effectiveDays.filter(d => d !== dayValue);
      // Prevent empty array - require at least one working day
      if (newDays.length === 0) {
        toast.error('At least one working day is required');
        return;
      }
    } else {
      newDays = [...effectiveDays, dayValue].sort((a, b) => a - b);
    }

    onChange?.(newDays);
  };

  const buttonSize = size === 'sm' ? 'w-9 h-9 text-xs' : 'w-10 h-10 text-sm';

  return (
    <div className="space-y-4">
      {showDefaultToggle && onToggleDefault && (
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">Use company default</Label>
          <Switch
            checked={useCompanyDefault}
            onCheckedChange={onToggleDefault}
            disabled={disabled}
          />
        </div>
      )}

      <div className="flex gap-2">
        {DAYS.map(({ day, value: dayValue }) => (
          <button
            key={day}
            type="button"
            onClick={() => toggleDay(dayValue)}
            disabled={disabled || useCompanyDefault}
            className={`${buttonSize} rounded-lg border-2 font-medium transition-all ${
              effectiveDays.includes(dayValue)
                ? 'bg-violet-600 text-white border-violet-600'
                : 'bg-white text-gray-400 border-gray-200'
            } ${
              (disabled || useCompanyDefault)
                ? 'opacity-60 cursor-not-allowed'
                : 'hover:border-gray-300'
            }`}
          >
            {day}
          </button>
        ))}
      </div>

      {showDefaultToggle && useCompanyDefault && (
        <p className="text-xs text-muted-foreground">
          Using company default: {formatWorkingDays(companyDefault)}
        </p>
      )}
    </div>
  );
}
